import { getJackpotWins } from '@namespace/games';
import {
  getAllTypesBonuses,
  getAllTypesBonusesHistory,
  getBonusTmplCms,
  getBsActiveBonus,
  getBsActiveDepositBonuses,
  getBsActiveWithdrawBonuses,
  getBsBonusDetails,
  getBsBonusesHistoryList,
  getBsBonusesList,
  getCrmBonusDetails,
  getCrmWagerBonuses,
  getFreeSpinBonusesCount,
  getFreeSpinBonusesList,
  getFundsBonusesList,
  getFundsCrmBonusesCount,
  getRiskFreeBonusesCount,
  getRiskFreeBonusesList
} from '../api';
import {
  BONUSES_COUNT_TO_LOAD,
  freeSpinStatesMap,
  fundsStatesMap,
  riskFreeStatesMap
} from '../constants';
import {
  normalizeAllTypesBonuses,
  normalizeBonusesList,
  normalizeBsBonusDetails,
  normalizeBsBonusesList
} from './normalizers';
import { useBonusesStore } from './store';

export const actionsCreator = ({ setState, getState }) => ({
  SET_IS_UPDATE_FREE_SPIN_BONUS_LIST: (isUpdateFreeSpinBonusList) =>
    setState({ isUpdateFreeSpinBonusList }),
  SET_IS_UPDATE_FUNDS_BONUS_LIST: (isUpdateFundsBonusList) =>
    setState({ isUpdateFundsBonusList }),
  FETCH_WAGER_BONUSES: async ({ isPreWagerBonusesEnabled }) => {
    const { response: crmWagerBonuses } = await getCrmWagerBonuses();
    let bsWagerBonus = {};

    if (isPreWagerBonusesEnabled) {
      const { data } = await getBsActiveBonus();
      bsWagerBonus = data;
    }

    setState({
      crmWagerBonuses,
      bsWagerBonus
    });
  },
  FETCH_ACTIVE_WITHDRAW_BONUSES: async (params) => {
    const { campaigns } = await getBsActiveWithdrawBonuses(params);

    setState({
      bsWithdrawBonuses: campaigns
    });
  },
  FETCH_ACTIVE_DEPOSIT_BONUSES: async () => {
    const { campaigns } = await getBsActiveDepositBonuses();

    setState({ bsDepositBonuses: campaigns });
  },
  FETCH_CRM_BONUS_DETAILS: async ({ bonusType, bonusData, noDataCallback }) => {
    try {
      const data = await getCrmBonusDetails(bonusData, bonusType);

      if (data) {
        setState({
          bonusDetails: data
        });
      } else {
        noDataCallback();
      }
    } catch (e) {
      throw new Error(e);
    }
  },
  RESET_BONUS_DETAILS: async () => {
    setState({
      bonusDetails: {}
    });
  },
  FETCH_BS_BONUS_DETAILS: async ({ bonusId, currency, formattedCurrency }) => {
    try {
      const bonus = await getBsBonusDetails(bonusId);
      const { contentTemplate } = bonus;
      const [bonusTmpl] = await getBonusTmplCms(contentTemplate);

      setState({
        bonusDetails: normalizeBsBonusDetails(
          bonus,
          currency,
          bonusTmpl,
          formattedCurrency
        )
      });
    } catch (e) {
      throw new Error(e);
    }
  },
  FETCH_FUNDS_BONUSES_DATA: async ({
    filter,
    platform,
    isPreWagerBonusesEnabled,
    currency,
    filterBonusesByCurrency
  }) => {
    try {
      const state = getState();
      let bonusesList = [];

      if (isPreWagerBonusesEnabled) {
        await bonusesActions.FETCH_WAGER_BONUSES({ isPreWagerBonusesEnabled });
        const { campaigns, totalCount: bsBonusesCount } =
          await getBsBonusesList();

        setState({
          bsFundsBonuses: {
            ...state.bsFundsBonuses,
            ...normalizeBonusesList(
              normalizeBsBonusesList(
                campaigns,
                currency,
                filterBonusesByCurrency
              )
            ),
            totalCount: bsBonusesCount
          }
        });
      }

      const { errorCode, response } = await getFundsBonusesList(
        {
          ...filter,
          pageSize: BONUSES_COUNT_TO_LOAD,
          stateList: fundsStatesMap.all
        },
        platform
      );
      const totalBonuses = await getFundsCrmBonusesCount(
        { ...filter, stateList: fundsStatesMap.all },
        platform
      );

      if (errorCode === 0) {
        bonusesList = response;
      }

      setState({
        crmFundsBonuses: {
          ...state.crmFundsBonuses,
          ...normalizeBonusesList(bonusesList),
          totalCount: totalBonuses
        }
      });
    } catch (e) {
      throw new Error(e);
    }
  },
  FETCH_MORE_FUNDS_BONUSES: async ({ filter, platform }) => {
    try {
      const state = getState();
      let bonusesList = [];
      const { errorCode, response } = await getFundsBonusesList(
        {
          ...filter,
          pageSize: BONUSES_COUNT_TO_LOAD,
          stateList: fundsStatesMap.all
        },
        platform
      );

      if (errorCode === 0) {
        bonusesList = response;
      }

      const { data, ids } = normalizeBonusesList(bonusesList);

      setState({
        crmFundsBonuses: {
          ...state.crmFundsBonuses,
          data: { ...state.crmFundsBonuses.data, ...data },
          ids: [...state.crmFundsBonuses.ids, ...ids]
        }
      });
    } catch {
      throw new Error();
    }
  },
  FETCH_FUNDS_BONUSES_HISTORY_DATA: async ({
    filter,
    platform,
    isPreWagerBonusesEnabled,
    currency,
    filterBonusesByCurrency
  }) => {
    try {
      const state = getState();
      let bonusesList = [];

      if (isPreWagerBonusesEnabled) {
        const { campaigns, totalCount: bsBonusesCount } =
          await getBsBonusesHistoryList();

        setState({
          bsFundsBonusesHistory: {
            ...state.bsFundsBonusesHistory,
            ...normalizeBonusesList(
              normalizeBsBonusesList(
                campaigns,
                currency,
                filterBonusesByCurrency
              )
            ),
            totalCount: bsBonusesCount
          }
        });
      }

      const { errorCode, response } = await getFundsBonusesList(
        {
          ...filter,
          pageSize: BONUSES_COUNT_TO_LOAD,
          stateList: fundsStatesMap.history
        },
        platform
      );
      const totalBonuses = await getFundsCrmBonusesCount(
        { ...filter, stateList: fundsStatesMap.history },
        platform
      );

      if (errorCode === 0) {
        bonusesList = response;
      }

      setState({
        crmFundsBonusesHistory: {
          ...state.crmFundsBonusesHistory,
          ...normalizeBonusesList(bonusesList),
          totalCount: totalBonuses
        }
      });
    } catch (e) {
      throw new Error(e);
    }
  },
  FETCH_MORE_FUNDS_BONUSES_HISTORY: async ({ filter, platform }) => {
    try {
      const state = getState();
      let bonusesList = [];
      const { errorCode, response } = await getFundsBonusesList(
        {
          ...filter,
          pageSize: BONUSES_COUNT_TO_LOAD,
          stateList: fundsStatesMap.history
        },
        platform
      );

      if (errorCode === 0) {
        bonusesList = response;
      }

      const { data, ids } = normalizeBonusesList(bonusesList);

      setState({
        crmFundsBonusesHistory: {
          ...state.crmFundsBonusesHistory,
          data: { ...state.crmFundsBonusesHistory.data, ...data },
          ids: [...state.crmFundsBonusesHistory.ids, ...ids]
        }
      });
    } catch {
      throw new Error();
    }
  },
  FETCH_FREE_SPINS_BONUSES_DATA: async ({ filter, platform }) => {
    try {
      const state = getState();
      let bonusesList = [];
      const promises = [
        await getFreeSpinBonusesList(
          {
            ...filter,
            pageSize: BONUSES_COUNT_TO_LOAD,
            stateList: freeSpinStatesMap.all
          },
          platform
        ),
        await getFreeSpinBonusesCount(
          { ...filter, stateList: freeSpinStatesMap.all },
          platform
        )
      ];

      const [{ errorCode, response }, totalBonuses] =
        await Promise.all(promises);

      if (errorCode === 0) {
        bonusesList = response;
      }

      setState({
        freeSpinBonuses: {
          ...state.freeSpinBonuses,
          ...normalizeBonusesList(bonusesList),
          totalCount: totalBonuses
        }
      });
    } catch (e) {
      throw new Error(e);
    }
  },
  FETCH_MORE_FREE_SPINS_BONUSES: async ({ filter, platform }) => {
    try {
      const state = getState();
      let bonusesList = [];
      const { errorCode, response } = await getFreeSpinBonusesList(
        {
          ...filter,
          pageSize: BONUSES_COUNT_TO_LOAD,
          stateList: freeSpinStatesMap.all
        },
        platform
      );

      if (errorCode === 0) {
        bonusesList = response;
      }

      const { data, ids } = normalizeBonusesList(bonusesList);

      setState({
        freeSpinBonuses: {
          ...state.freeSpinBonuses,
          data: { ...state.freeSpinBonuses.data, ...data },
          ids: [...state.freeSpinBonuses.ids, ...ids]
        }
      });
    } catch {
      throw new Error();
    }
  },
  FETCH_FREE_SPINS_BONUSES_HISTORY_DATA: async ({ filter, platform }) => {
    try {
      const state = getState();
      let bonusesList = [];
      const promises = [
        await getFreeSpinBonusesList(
          {
            ...filter,
            pageSize: BONUSES_COUNT_TO_LOAD,
            stateList: freeSpinStatesMap.history
          },
          platform
        ),
        await getFreeSpinBonusesCount(
          { ...filter, stateList: freeSpinStatesMap.history },
          platform
        )
      ];

      const [{ errorCode, response }, totalBonuses] =
        await Promise.all(promises);

      if (errorCode === 0) {
        bonusesList = response;
      }

      setState({
        freeSpinBonusesHistory: {
          ...state.freeSpinBonusesHistory,
          ...normalizeBonusesList(bonusesList),
          totalCount: totalBonuses
        }
      });
    } catch (e) {
      throw new Error(e);
    }
  },
  FETCH_MORE_FREE_SPINS_BONUSES_HISTORY: async ({ filter, platform }) => {
    try {
      const state = getState();
      let bonusesList = [];
      const { errorCode, response } = await getFreeSpinBonusesList(
        {
          ...filter,
          pageSize: BONUSES_COUNT_TO_LOAD,
          stateList: freeSpinStatesMap.history
        },
        platform
      );

      if (errorCode === 0) {
        bonusesList = response;
      }

      const { data, ids } = normalizeBonusesList(bonusesList);

      setState({
        freeSpinBonusesHistory: {
          ...state.freeSpinBonusesHistory,
          data: { ...state.freeSpinBonusesHistory.data, ...data },
          ids: [...state.freeSpinBonusesHistory.ids, ...ids]
        }
      });
    } catch {
      throw new Error();
    }
  },
  FETCH_RISK_FREE_BONUSES_DATA: async ({ filter, platform }) => {
    try {
      const state = getState();
      let bonusesList = [];
      const promises = [
        await getRiskFreeBonusesList(
          {
            ...filter,
            pageSize: BONUSES_COUNT_TO_LOAD,
            stateList: riskFreeStatesMap.all
          },
          platform
        ),
        await getRiskFreeBonusesCount(
          { ...filter, stateList: riskFreeStatesMap.all },
          platform
        )
      ];

      const [{ errorCode, response }, totalBonuses] =
        await Promise.all(promises);

      if (errorCode === 0) {
        bonusesList = response;
      }

      setState({
        riskFreeBonuses: {
          ...state.riskFreeBonuses,
          ...normalizeBonusesList(bonusesList),
          totalCount: totalBonuses
        }
      });
    } catch (e) {
      throw new Error(e);
    }
  },
  FETCH_MORE_RISK_FREE_BONUSES: async ({ filter, platform }) => {
    try {
      const state = getState();
      let bonusesList = [];
      const { errorCode, response } = await getRiskFreeBonusesList(
        {
          ...filter,
          pageSize: BONUSES_COUNT_TO_LOAD,
          stateList: riskFreeStatesMap.all
        },
        platform
      );

      if (errorCode === 0) {
        bonusesList = response;
      }

      const { data, ids } = normalizeBonusesList(bonusesList);

      setState({
        riskFreeBonuses: {
          ...state.riskFreeBonuses,
          data: { ...state.riskFreeBonuses.data, ...data },
          ids: [...state.riskFreeBonuses.ids, ...ids]
        }
      });
    } catch {
      throw new Error();
    }
  },
  FETCH_RISK_FREE_BONUSES_HISTORY_DATA: async ({ filter, platform }) => {
    try {
      const state = getState();
      let bonusesList = [];
      const promises = [
        await getRiskFreeBonusesList(
          {
            ...filter,
            pageSize: BONUSES_COUNT_TO_LOAD,
            stateList: riskFreeStatesMap.history
          },
          platform
        ),
        await getRiskFreeBonusesCount(
          { ...filter, stateList: riskFreeStatesMap.history },
          platform
        )
      ];

      const [{ errorCode, response }, totalBonuses] =
        await Promise.all(promises);

      if (errorCode === 0) {
        bonusesList = response;
      }

      setState({
        riskFreeBonusesHistory: {
          ...state.riskFreeBonusesHistory,
          ...normalizeBonusesList(bonusesList),
          totalCount: totalBonuses
        }
      });
    } catch (e) {
      throw new Error(e);
    }
  },
  FETCH_MORE_RISK_FREE_BONUSES_HISTORY: async ({ filter, platform }) => {
    try {
      const state = getState();
      let bonusesList = [];
      const { errorCode, response } = await getRiskFreeBonusesList(
        {
          ...filter,
          pageSize: BONUSES_COUNT_TO_LOAD,
          stateList: riskFreeStatesMap.history
        },
        platform
      );

      if (errorCode === 0) {
        bonusesList = response;
      }

      const { data, ids } = normalizeBonusesList(bonusesList);

      setState({
        riskFreeBonusesHistory: {
          ...state.riskFreeBonusesHistory,
          data: { ...state.riskFreeBonusesHistory.data, ...data },
          ids: [...state.riskFreeBonusesHistory.ids, ...ids]
        }
      });
    } catch {
      throw new Error();
    }
  },
  FETCH_JACKPOT_WINS: async () => {
    try {
      const { status, data } = await getJackpotWins();

      if (status === 'success') {
        setState({
          jackpotsWinsBonuses: data
        });
      }
    } catch (e) {
      throw new Error(e);
    }
  },
  FETCH_ALL_TYPES_BONUSES: async ({ currency }) => {
    try {
      const { bonuses, pagination } = await getAllTypesBonuses({
        pagination: {
          limit: BONUSES_COUNT_TO_LOAD,
          page: 1
        }
      });

      const { nextPageExist } = pagination;

      const formattedBonuses = normalizeAllTypesBonuses({ bonuses, currency });

      setState({
        allTypesBonuses: {
          ...normalizeBonusesList(formattedBonuses),
          isNextPageExist: nextPageExist
        }
      });
    } catch (e) {
      throw new Error(e);
    }
  },
  FETCH_MORE_ALL_TYPES_BONUSES: async ({ page, currency }) => {
    try {
      const state = getState();
      const { bonuses, pagination } = await getAllTypesBonuses({
        pagination: {
          limit: BONUSES_COUNT_TO_LOAD,
          page
        }
      });

      const { nextPageExist } = pagination;

      const formattedBonuses = normalizeAllTypesBonuses({ bonuses, currency });

      const { data, ids } = normalizeBonusesList(formattedBonuses);

      setState({
        allTypesBonuses: {
          data: { ...state.allTypesBonuses.data, ...data },
          ids: [...state.allTypesBonuses.ids, ...ids],
          isNextPageExist: nextPageExist
        }
      });
    } catch (e) {
      throw new Error(e);
    }
  },
  FETCH_ALL_TYPES_BONUSES_HISTORY: async ({ currency }) => {
    try {
      const { bonuses, pagination } = await getAllTypesBonusesHistory({
        pagination: {
          limit: BONUSES_COUNT_TO_LOAD,
          page: 1
        }
      });

      const { nextPageExist } = pagination;

      const formattedBonuses = normalizeAllTypesBonuses({ bonuses, currency });

      setState({
        allTypesBonusesHistory: {
          ...normalizeBonusesList(formattedBonuses),
          isNextPageExist: nextPageExist
        }
      });
    } catch (e) {
      throw new Error(e);
    }
  },
  FETCH_MORE_ALL_TYPES_BONUSES_HISTORY: async ({ page, currency }) => {
    try {
      const state = getState();
      const { bonuses, pagination } = await getAllTypesBonusesHistory({
        pagination: {
          limit: BONUSES_COUNT_TO_LOAD,
          page
        }
      });

      const { nextPageExist } = pagination;

      const formattedBonuses = normalizeAllTypesBonuses({ bonuses, currency });

      const { data, ids } = normalizeBonusesList(formattedBonuses);

      setState({
        allTypesBonusesHistory: {
          data: { ...state.allTypesBonusesHistory.data, ...data },
          ids: [...state.allTypesBonusesHistory.ids, ...ids],
          isNextPageExist: nextPageExist
        }
      });
    } catch (e) {
      throw new Error(e);
    }
  }
});

export const bonusesActions = actionsCreator(useBonusesStore);
